import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import { convertFromSeconds, createTimer, editTimer, deleteTimer, getTimers } from '../../../services/timers';
import { rtcBufferActions } from '../../../redux/actions';
import T, { Translate } from '../../i18n/i18n';
import * as Tags from './Timers.styles';

const Timers = ({tableId}) => {

    const [timers, setTimers] = useState([]);
    const [updateCount, setUpdateCount] = useState(0);
    const [isFormOpened, setIsFormOpened] = useState(false);
    const [timerId, setTimerId] = useState();
    const [name, setName] = useState('');
    const [type, setType] = useState('countup');
    const [seconds, setSeconds] = useState(0);
    const [events, setEvents] = useState([]);
    const [clockState, setClockState] = useState('stopped');
    const [clockTime, setClockTime] = useState('00:00:00');

    const opened = useSelector(state => state.timersState.opened);
    const openedTurnOrder = useSelector(state => state.turnorderState.opened);
    const itemsSync = useSelector(state => state.timersState.items);
    const isOwner = useSelector(state => state.authState.isOwner);

    const refUpdateTimers = useRef();
    const dispatch = useDispatch();


    const handleOpenAdd = () => {
        if (!isOwner) {
            return;
        }

        setIsFormOpened(true);
        setTimerId(undefined);
        setName('');
        setType('countup');
        setEvents([]);
        setSeconds(0);
        setClockState('stopped');
        setClockTime('00:00:00');
    }

    const handleChangeClockTime = (e) => {
        setClockTime(e.target.value);

        const [hours, minutes, seconds] = e.target.value.split(':');
        const value = (parseInt(hours) * 3600000) + (parseInt(minutes) * 60000) + (parseInt(seconds) * 1000);
        setSeconds(value);
        setEvents([{type: "reset", date: value}]);
    }

    const handleCancel = () => {
        setTimerId(undefined);
        setIsFormOpened(false);
    }

    const handleDelete = () => {
        deleteTimer(tableId, timerId).then(() => {
            setTimerId(undefined);
            setIsFormOpened(false);

            let newTimers = timers.filter((timer) => timer.id !== timerId);
            setTimers(newTimers);

            dispatch(rtcBufferActions.pushOutputBuffer({
                type: 'timersChange',
                data: {
                    action: 'removed',
                    timers: newTimers
                }
            }));
        });

    }
    
    const handlePlay = () => {
        editTimer(tableId, timerId, {name, type, 
            state:"running", 
            events: [...events, {
                type: "start",
                date: new Date().getTime()
            }],
        }).then((res) => {
            setClockState('running');
            setSeconds(res.payload.seconds);
            setClockTime(convertFromSeconds(res.payload.seconds));
            setEvents(res.payload.events);

            let newTimers = timers.map((timer) => {
                if (timer.id === timerId) {
                    return res.payload;
                }
                return timer;
            });

            setTimers(newTimers);
            dispatch(rtcBufferActions.pushOutputBuffer({
                type: 'timersChange',
                data: {
                    action: 'play',
                    timers: newTimers
                }
            }));
        });
    }
    
    const handlePause = () => {
        editTimer(tableId, timerId, {name, type, 
            state:"paused", 
            events: [...events, {
                type: "pause",
                date: new Date().getTime()
            }],
        }).then((res) => {
            setClockState('paused');
            setSeconds(res.payload.seconds);
            setClockTime(convertFromSeconds(res.payload.seconds));
            setEvents(res.payload.events);

            let newTimers = timers.map((timer) => {
                if (timer.id === timerId) {
                    return res.payload;
                }
                return timer;
            });
            setTimers(newTimers);

            dispatch(rtcBufferActions.pushOutputBuffer({
                type: 'timersChange',
                data: {
                    action: 'pause',
                    timers: newTimers
                }
            }));
        });
    }
    
    const handleStop = () => {
        editTimer(tableId, timerId, {name, type, 
            state: "stopped", 
            events: [...events, {
                type: "stop",
                date: new Date().getTime()
            }],
        }).then((res) => {
            setClockState('stopped');
            setSeconds(res.payload.seconds);
            setClockTime(convertFromSeconds(res.payload.seconds));
            setEvents(res.payload.events);

            let newTimers = timers.map((timer) => {
                if (timer.id === timerId) {
                    return res.payload;
                }
                return timer;
            });
            setTimers(newTimers);

            dispatch(rtcBufferActions.pushOutputBuffer({
                type: 'timersChange',
                data: {
                    action: 'stop',
                    timers: newTimers
                }
            }));
        });
    }

    const handleEditTimer = (timer) => {
        if (!isOwner) {
            return;
        }

        setIsFormOpened(true);
        setTimerId(timer.id);
        setName(timer.name);
        setType(timer.type);
        setEvents(timer.events);
        setClockState(timer.state);
        setSeconds(timer.seconds);
        setClockTime(convertFromSeconds(timer.seconds));
    }

    const handleSave = () => {
        if (timerId) {
            editTimer(tableId, timerId, {name, type, seconds, events}).then((res) => {
                setIsFormOpened(false);
                
                let newTimers = timers.map((timer) => {
                    if (timer.id === timerId) {
                        return res.payload;
                    }
                    return timer;
                });
                setTimers(newTimers);
                dispatch(rtcBufferActions.pushOutputBuffer({
                    type: 'timersChange',
                    data: {
                        action: 'created',
                        timers: newTimers
                    }
                }));
            });
        } else {
            createTimer(tableId, {name, type, seconds, events}).then((res) => {
                setIsFormOpened(false);

                let newTimers = [...timers, res.payload];
                setTimers(newTimers);

                dispatch(rtcBufferActions.pushOutputBuffer({
                    type: 'timersChange',
                    data: {
                        action: 'edited',
                        timers: newTimers
                    }
                }));
            });
        }
    }

    const handleTimersRebuild = (t) => {
        let newTimers = t.map(timer => {
            if (timer.state === 'running') {
                let now = new Date().getTime();
                let diff = now - timer.events[timer.events.length - 1].date;
                timer.seconds += timer.type === 'countup' ? diff : -diff;
            }

            return timer;
        });
        setTimers(newTimers);
    }


    useEffect(() => {
        getTimers(tableId).then((res) => {
            let timers = res.payload ?? [];
            handleTimersRebuild(timers);
        });
    }, [tableId]);

    useEffect(() => {
        handleTimersRebuild(itemsSync || []);
    }, [itemsSync]);

    useEffect(() => {
        setTimers(timers => {
            if (timers.every(t => t.state !== 'running')) {
                return timers;
            }

            let sholdUpdate = false;
            let timers2 = timers.map(t => {
                if (t && t.state === 'running') {
                    sholdUpdate = true;
                    let now = new Date().getTime();
                    let diff = now - (t.events[t.events.length-1] ?? {date:0}).date;
                    return {...t, seconds: t.startSeconds + (t.type === 'countup' ? diff : -diff)};
                }
                return t;
            });

            if (sholdUpdate) {
                let openedTimer = timers2.find((t) => t.id === timerId);
                if (openedTimer) {
                    setSeconds(openedTimer.seconds);
                    setClockTime(convertFromSeconds(openedTimer.seconds));
                } 
                return timers2;
            }

            return timers;
        });
    }, [updateCount]);

    useEffect(() => {
        refUpdateTimers.current = setInterval(() => {
            setUpdateCount(v => v+1);
        }, 1000);

        return () => {
            if (refUpdateTimers.current) {
                clearInterval(refUpdateTimers.current);
            }
        }
    }, []);


    return <Tags.Container className={(opened ? 'opened' : '') + (openedTurnOrder ? ' turnOrderOpened' : '')}>
        <Tags.Header>
            <T value='timers.title' />
        </Tags.Header>

        {isFormOpened ? <>
            <Tags.Form>
                <Tags.Label><T value="timers.name" /></Tags.Label>
                <Tags.Input type='text' placeholder='Name' value={name} onChange={(e) => setName(e.target.value)} />
                <Tags.Row>
                    <Tags.Label><T value="timers.type" /></Tags.Label>
                    <Tags.Label><T value="timers.clockTime" /></Tags.Label>
                </Tags.Row>
                <Tags.Row>
                    <Tags.Select disabled={clockState === 'running'} value={type} onChange={(e) => setType(e.target.value)}>
                        <option value='countup'>{Translate("timers.clockup")}</option>
                        <option value='countdown'>{Translate("timers.clockdown")}</option>
                    </Tags.Select>
                    <Tags.Input type="time" step={1} disabled={clockState === 'running'} value={clockTime} onChange={(e) => handleChangeClockTime(e)} />
                </Tags.Row>
            </Tags.Form>
            <Tags.ButtonRow>
                <Tags.Button color="#999" onClick={() => handleCancel()}>
                    <Tags.Icon title={Translate("cancel")} color="#999" icon="F004D" />
                </Tags.Button>
                {timerId ? <>
                    {clockState !== 'running' ? <Tags.Button color="#2bf" onClick={() => handlePlay()}>
                        <Tags.Icon title={Translate("timers.play")} color="#2bf" icon="F040D" />
                    </Tags.Button> : <Tags.Button color="#ff3" onClick={() => handlePause()}>
                        <Tags.Icon title={Translate("timers.pause")} color="#ff3" icon="F03E6" />
                    </Tags.Button>}
                    {clockState !== 'stopped' && type !== 'countdown' ? <Tags.Button color="#fa2" onClick={() => handleStop()}>
                        <Tags.Icon title={Translate("timers.stop")} color="#fa2" icon="F0667" />
                    </Tags.Button> : null }
                    <div style={{flex: 1}}></div>
                    <Tags.Button color="#f33" onClick={() => handleDelete()}>
                        <Tags.Icon title={Translate("delete")} color="#f33" icon="F04DB" />
                    </Tags.Button>
                </> : <div style={{flex: 1}}></div>}
                <Tags.Button onClick={() => handleSave()}>
                    <Tags.Icon title={Translate("save")} color="#0c0" icon="F012C" />
                </Tags.Button>
            </Tags.ButtonRow>
        </> : null}
        
        {!isFormOpened ? <>
            {timers.length ? <Tags.TimerList className="scrollbar">
                {timers.map((timer, index) => {
                    return timer && <Tags.Timer key={index} onClick={()=>handleEditTimer(timer)} className={timer.state}>
                        <Tags.TimerType className={timer.type}></Tags.TimerType>
                        <Tags.TimerLabel>
                            <Tags.TimerName>{timer.name}</Tags.TimerName>
                            <Tags.TimerRow>
                                <Tags.TimerState><T value={"timers.state"+timer.state} /></Tags.TimerState>
                                <Tags.TimerValue>{convertFromSeconds(timer.seconds)}</Tags.TimerValue>
                            </Tags.TimerRow>
                        </Tags.TimerLabel>
                    </Tags.Timer>
                })}
            </Tags.TimerList> : <Tags.NoTimers>
                <T value={isOwner ? "timers.nodata" : "timers.nodata"} />
            </Tags.NoTimers>}
            {isOwner ? <Tags.ButtonRow>
                <Tags.Button onClick={handleOpenAdd} style={{flex:1}}>
                    <T value='timers.add' />
                </Tags.Button>
            </Tags.ButtonRow> : null}
        </> : null}

    </Tags.Container>
}

export default Timers;